/**
 * Crypto Ticker URL Mode
 * 
 * Displays cryptocurrency prices in a scrolling marquee format in the URL hash.
 */

export interface TickerState {
  prices: Array<{ symbol: string; price: number; change24h: number }>;
  lastFetch: number;
  lastUpdate: number;
}

/**
 * Create ticker state
 */
export function createTickerState(): TickerState {
  return {
    prices: [],
    lastFetch: 0,
    lastUpdate: 0,
  };
}

/**
 * Fetch cryptocurrency prices from CoinGecko API
 */
export async function fetchCryptoPrices(state: TickerState): Promise<void> {
  const now = Date.now();
  // Cache prices for 30 seconds to avoid rate limiting
  if (now - state.lastFetch < 30000 && state.prices.length > 0) {
    return;
  }

  try {
    // Use CoinGecko's free API - no API key needed for basic price data
    const response = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,binancecoin,solana,cardano&vs_currencies=usd&include_24hr_change=true'
    );

    if (!response.ok) {
      throw new Error('Failed to fetch crypto prices');
    }

    const data = await response.json();

    // Map CoinGecko IDs to symbols
    const symbolMap: Record<string, string> = {
      bitcoin: 'BTC',
      ethereum: 'ETH',
      binancecoin: 'BNB',
      solana: 'SOL',
      cardano: 'ADA',
    };

    state.prices = Object.entries(data)
      .map(([id, priceData]: [string, any]) => ({
        symbol: symbolMap[id] || id.toUpperCase(),
        price: priceData.usd,
        change24h: priceData.usd_24h_change || 0,
      }))
      .filter(p => p.symbol); // Filter out any unmapped entries

    state.lastFetch = now;
  } catch (error) {
    // Silently fail - we'll use empty prices or cached data
  }
}

/**
 * Generate ticker string for URL hash
 */
export function generateTickerString(state: TickerState): string {
  if (state.prices.length === 0) {
    return '...';
  }

  // Build ticker string with prices - ultra-minimal format
  // Format: "BTC12345↑ ETH2345↓ SOL123↑" (no spaces, no $, no %, arrows for direction)
  const tickerItems = state.prices.map(crypto => {
    // Round price to nearest integer for minimalism
    const price = Math.round(crypto.price);
    // Use arrow symbols for direction (↑ for up, ↓ for down, → for neutral)
    const direction = crypto.change24h > 0.1 ? '↑' : crypto.change24h < -0.1 ? '↓' : '→';
    return `${crypto.symbol}${price}${direction}`;
  });

  // Join with multiple underscores for better legibility
  const fullTicker = tickerItems.join('___') + '___';

  // Create marquee effect by rotating the offset
  // Update offset based on time to create scrolling effect
  const now = Date.now();
  const scrollSpeed = 0.2; // Characters per 100ms (slower scrolling)
  const offset = Math.floor((now / 100) * scrollSpeed) % fullTicker.length;

  // Create scrolling string
  const scrolled = fullTicker.slice(offset) + fullTicker.slice(0, offset);

  // Limit to reasonable URL length (max ~200 chars for hash)
  return scrolled.slice(0, 200);
}

